/**
 * Carbon menu button: a labelled button with a trailing chevron that opens
 * the OverflowMenu's layer-02 menu (headless UI Menu: arrow keys, type-ahead,
 * Escape; through a portal, so a scroller never clips it). The visible label
 * is the button's accessible name ("Inactive after 90 days"). Items with
 * `selected` choose one value: the chosen one carries Carbon's check.
 */
import { useState } from 'react'
import { Menu, Portal } from '@headlessui/react'
import clsx from 'clsx'
import { Button } from './Button'
import { IconChevronDown } from './icons'
import { MenuPanel, type OverflowMenuItem } from './OverflowMenu'

export interface MenuButtonProps {
  /** The visible label, and the button's accessible name. */
  label: string
  items: OverflowMenuItem[]
  /** ghost (default) for a toolbar or tab-strip control; tertiary beside a primary action. */
  kind?: 'ghost' | 'tertiary'
  /** Trigger height: sm 32px, md 40px (default), lg 48px. */
  size?: 'sm' | 'md' | 'lg'
  /** Which edge of the trigger the menu lines up with. Default start. */
  align?: 'start' | 'end'
  disabled?: boolean
  className?: string
}

export function MenuButton({ label, items, kind = 'ghost', size = 'md', align = 'start', disabled, className }: MenuButtonProps) {
  const [trigger, setTrigger] = useState<HTMLElement | null>(null)
  return (
    <Menu as="div" className={clsx('relative inline-flex flex-none', className)}>
      {({ open }) => (
        <>
          <Menu.Button
            as={Button}
            ref={setTrigger}
            kind={kind}
            size={size}
            disabled={disabled}
            icon={<IconChevronDown className={clsx('transition-transform duration-110 ease-carbon', open && 'rotate-180')} />}
            // While open the trigger keeps its hover fill (variant classes sort after the kind's own fill).
            className={
              kind === 'ghost'
                ? 'aria-expanded:bg-layer-hover aria-expanded:text-link-hover'
                : 'aria-expanded:bg-text-primary aria-expanded:text-[color:var(--background)]'
            }
          >
            {label}
          </Menu.Button>
          {open ? (
            <Portal>
              <MenuPanel anchor={trigger} align={align} items={items} />
            </Portal>
          ) : null}
        </>
      )}
    </Menu>
  )
}

export default MenuButton
